import {
	Bell,
	BookOpen,
	Camera,
	Cloud,
	Coffee,
	Flower2,
	Gift,
	Globe,
	Headphones,
	Heart,
	Image,
	Lightbulb,
	MapPin,
	MessageCircle,
	Moon,
	Music,
	Palette,
	PartyPopper,
	Pencil,
	Phone,
	Plane,
	Rainbow,
	Rocket,
	Send,
	Sparkles,
	Smile,
	Star,
	Sun,
	ThumbsUp,
	TreePine,
	Umbrella,
	User,
	Video,
	Waves,
	X,
	Zap,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { formatPhoneDisplay } from '@/utils/participantDetailDisplay';
import { WhatsAppMessageReceipt } from '@/components/whatsapp/WhatsAppMessageReceipt';
import {
	formatSessionWhatsAppMessageTime,
	getSessionWhatsAppMessageDisplayTime,
	getSessionWhatsAppReceiptLevel,
} from '@/utils/sessionWhatsAppDisplay';

type WhatsAppMessagePreviewProps = {
	isOpen: boolean;
	onClose: () => void;
	message: string;
	patientName?: string;
	patientPhone?: string | null;
	notification?: SessionWhatsApp.SessionWhatsAppNotification | null;
};

type LucideIconComponent = typeof Camera;

type BackgroundIcon = {
	Icon: LucideIconComponent;
	top: string;
	left: string;
	rotate: number;
};

const BACKGROUND_ICON_SIZE = 20;
const BACKGROUND_ICON_COLOR = '#b8aea4';
const BACKGROUND_ICON_OPACITY = 0.14;
const BACKGROUND_ICON_STROKE = 1.5;

const WA_CHAT_BG = '#ECE5DD';
const WA_FOOTER_BG = '#D8D0C8';
const WA_FOOTER_BORDER = '#C9C0B8';
const WA_FOOTER_TEXT = '#8A827A';

const ICON_POOL: LucideIconComponent[] = [
	Camera,
	Cloud,
	Coffee,
	Heart,
	Lightbulb,
	MessageCircle,
	Music,
	Smile,
	Star,
	Sun,
	Bell,
	BookOpen,
	Flower2,
	Gift,
	Globe,
	Headphones,
	Image,
	MapPin,
	Moon,
	Palette,
	PartyPopper,
	Pencil,
	Phone,
	Plane,
	Rainbow,
	Rocket,
	Send,
	Sparkles,
	ThumbsUp,
	TreePine,
	Umbrella,
	Video,
	Waves,
	Zap,
];

const BACKGROUND_ICON_COUNT = 200;

const BACKGROUND_ICONS: BackgroundIcon[] = Array.from(
	{ length: BACKGROUND_ICON_COUNT },
	(_, index) => {
		const cols = 20;
		const rows = Math.ceil(BACKGROUND_ICON_COUNT / cols);
		const col = index % cols;
		const row = Math.floor(index / cols);
		const cellW = 100 / cols;
		const cellH = 100 / rows;
		const jitterX = ((index * 17) % 4) - 1.5;
		const jitterY = ((index * 13) % 4) - 1.5;
		const left = Math.min(
			98,
			Math.max(0, col * cellW + cellW * 0.28 + jitterX),
		);
		const top = Math.min(
			98,
			Math.max(0, row * cellH + cellH * 0.22 + jitterY),
		);

		return {
			Icon: ICON_POOL[index % ICON_POOL.length],
			top: `${top}%`,
			left: `${left}%`,
			rotate: ((index * 23) % 50) - 25,
		};
	},
);

const getPatientInitial = (name: string): string =>
	name.trim().charAt(0).toUpperCase() || '?';

const WhatsAppOutgoingTail = () => (
	<svg
		viewBox="0 0 8 13"
		width="8"
		height="13"
		className="absolute -right-[6px] top-0 text-[#DCF8C6]"
		aria-hidden
	>
		<path
			fill="currentColor"
			d="M5.188 0H0v11.193l6.467-8.625C7.526 1.156 6.958 0 5.188 0z"
		/>
	</svg>
);

const WhatsAppChatBackground = () => (
	<div
		className="pointer-events-none absolute inset-0 overflow-hidden"
		aria-hidden
	>
		{BACKGROUND_ICONS.map(({ Icon, top, left, rotate }, index) => (
			<Icon
				key={`wa-bg-icon-${index}`}
				className="absolute"
				strokeWidth={BACKGROUND_ICON_STROKE}
				style={{
					top,
					left,
					width: BACKGROUND_ICON_SIZE,
					height: BACKGROUND_ICON_SIZE,
					transform: `rotate(${rotate}deg)`,
					color: BACKGROUND_ICON_COLOR,
					opacity: BACKGROUND_ICON_OPACITY,
				}}
			/>
		))}
	</div>
);

const WhatsAppMessagePreview = ({
	isOpen,
	onClose,
	message,
	patientName,
	patientPhone,
	notification = null,
}: WhatsAppMessagePreviewProps) => {
	if (!isOpen) return null;

	const receiptLevel = notification
		? getSessionWhatsAppReceiptLevel(notification)
		: 'read';
	const displayTime = notification
		? getSessionWhatsAppMessageDisplayTime(notification)
		: formatSessionWhatsAppMessageTime(new Date().toISOString());
	const isIllustrative = notification == null || !notification.sent_at;
	const phoneLabel =
		formatPhoneDisplay(patientPhone) ?? patientPhone?.trim() ?? '';

	return createPortal(
		<div className="fixed inset-0 z-[10000] flex items-center justify-center bg-stone-900/70 p-4 backdrop-blur-[2px]">
			<button
				type="button"
				className="absolute inset-0 cursor-default"
				onClick={onClose}
				aria-label="Fechar pré-visualização"
			/>
			<div
				className="animate-in zoom-in-95 relative flex w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl duration-200"
				role="dialog"
				aria-modal="true"
				aria-label="Pré-visualização da mensagem WhatsApp"
			>
				<div className="flex items-center gap-3 bg-[#075E54] px-4 py-3 text-white">
					{patientName ? (
						<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold uppercase text-blue-700 ring-2 ring-blue-200">
							{getPatientInitial(patientName)}
						</div>
					) : (
						<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
							<User className="size-5" strokeWidth={2.25} />
						</div>
					)}
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-semibold">
							{patientName?.trim() || 'Paciente'}
						</p>
						{phoneLabel ? (
							<p className="truncate text-[11px] text-emerald-100/90">
								{phoneLabel}
							</p>
						) : null}
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-full p-1 text-white/80 transition hover:bg-white/10 hover:text-white"
						aria-label="Fechar"
					>
						<X size={18} />
					</button>
				</div>

				<div
					className="relative min-h-[300px] overflow-hidden px-4 py-5"
					style={{ backgroundColor: WA_CHAT_BG }}
				>
					<WhatsAppChatBackground />
					<div className="relative z-[1]">
						<div className="mb-4 flex justify-center">
							<span className="rounded-md bg-white/90 px-3 py-1 text-[11px] font-medium text-stone-500 shadow-sm">
								Hoje
							</span>
						</div>
						<div className="relative ml-auto max-w-[92%]">
							<div className="relative rounded-lg rounded-tr-none bg-[#DCF8C6] px-3 pb-1.5 pt-2 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)]">
								<WhatsAppOutgoingTail />
								<p className="whitespace-pre-wrap break-words text-[13.5px] leading-[1.35] text-stone-900">
									{message}
								</p>
								<div className="mt-0.5 flex items-end justify-end gap-1">
									{displayTime ? (
										<span className="text-[11px] leading-none text-stone-500/70">
											{displayTime}
										</span>
									) : null}
									<WhatsAppMessageReceipt
										level={receiptLevel}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					className="px-4 py-2.5 text-center"
					style={{
						backgroundColor: WA_FOOTER_BG,
						borderTop: `1px solid ${WA_FOOTER_BORDER}`,
					}}
				>
					<p
						className="text-[11px] font-normal"
						style={{ color: WA_FOOTER_TEXT }}
					>
						{isIllustrative
							? 'Pré-visualização — o horário exibido é ilustrativo.'
							: 'Pré-visualização com horário e status reais da notificação.'}
					</p>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export { WhatsAppMessagePreview };
